import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AdminDashboard from "../AdminDashboard/admindash";
import {
  PenTool,
  Image as ImageIcon,
  Tag,
  User as UserIcon,
  Eye,
  Save,
  CheckCircle,
  Loader2,
  FileText,
  Sparkles,
  Zap,
  ChevronLeft
} from 'lucide-react';
import { toast } from 'react-toastify';
import API_URL from "../../config";


const AddBlog = () => {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Travel');
  const [content, setContent] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [author, setAuthor] = useState('Travel & Explore Community');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleAddBlog = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const blogData = { title, category, content, coverImageUrl, author };

    try {
      const response = await fetch(`${API_URL}/blogs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blogData),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Narrative published to the global stream.');
        // Brief delay for feedback before redirecting to view
        setTimeout(() => navigate('/view'), 1500);
      } else {
        toast.error(result.message || 'Identity publication failed.');
      }
    } catch (err) {
      toast.error('Terminal server error during publication.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdminDashboard>
      <div className="max-w-7xl mx-auto font-sans">

        {/* Header Navigation */}
        <div className="flex justify-between items-end mb-12 border-b border-gray-100 pb-10">
          <div>
            <Link to="/view" className="inline-flex items-center gap-2 text-sky-600 font-black text-[10px] uppercase tracking-widest mb-4 hover:-translate-x-1 transition-transform">
              <ChevronLeft size={14} /> Back to Registry
            </Link>
            <h1 className="text-3xl font-black text-sky-950 tracking-tight flex items-center gap-4 leading-none">
              <Plus className="text-sky-500" size={36} />
              Compose <span className="text-sky-600 italic">New Discovery</span>
            </h1>
            <p className="text-gray-400 mt-3 font-bold text-sm max-w-lg">Share unique perspectives and untold stories with the TravelAura Elite community.</p>
          </div>
          <div className="hidden lg:flex px-6 py-4 bg-sky-50 rounded-2xl border border-sky-100 items-center gap-4">
            <Zap className="text-amber-400" size={24} />
            <div>
              <p className="text-[9px] font-black text-sky-900/40 uppercase tracking-widest">Global Feed</p>
              <h4 className="text-xs font-black text-sky-900 tracking-tighter">LIVE SYNC ACTIVE</h4>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Editor Column */}
          <div className="lg:col-span-7 space-y-8">
            <div className="bg-white rounded-[3rem] shadow-xl border border-gray-100 overflow-hidden ring-1 ring-gray-50">
              <div className="bg-gray-50/50 px-10 py-6 border-b border-gray-100 flex items-center justify-between">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <FileText size={14} className="text-sky-300" /> Editorial Desk
                </span>
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-rose-200"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-200"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-200 shadow-inner"></div>
                </div>
              </div>

              <form onSubmit={handleAddBlog} className="p-10 space-y-8">
                {/* Title */}
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-sky-950 uppercase tracking-widest flex items-center gap-2">
                    <Tag size={12} className="text-sky-300" /> Story Identity
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter a captivating title..."
                    required
                    className="w-full px-6 py-5 bg-gray-50 border-2 border-transparent rounded-[1.5rem] focus:ring-4 focus:ring-sky-500/10 focus:border-sky-500 focus:bg-white transition-all text-xl font-black text-sky-950 placeholder:text-gray-200"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Category */}
                  <div className="space-y-3">
                    <label className="text-[11px] font-black text-sky-950 uppercase tracking-widest">Target Context</label>
                    <div className="relative">
                      <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-sky-500 transition-all font-black text-xs uppercase tracking-widest text-sky-900 appearance-none cursor-pointer"
                      >
                        <option value="Travel">Travel</option>
                        <option value="Adventure">Adventure</option>
                        <option value="Events">Events</option>
                        <option value="Food">Food</option>
                      </select>
                      <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-sky-300">
                        <Tag size={16} />
                      </div>
                    </div>
                  </div>

                  {/* Author */}
                  <div className="space-y-3">
                    <label className="text-[11px] font-black text-sky-950 uppercase tracking-widest flex items-center gap-2">
                      <UserIcon size={12} className="text-sky-300" /> Lead Author
                    </label>
                    <input
                      type="text"
                      value={author}
                      onChange={(e) => setAuthor(e.target.value)}
                      className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-sky-500 transition-all font-bold text-sky-950"
                    />
                  </div>
                </div>

                {/* Cover Image URL */}
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-sky-950 uppercase tracking-widest flex items-center gap-2">
                    <ImageIcon size={12} className="text-sky-300" /> Narrative Visual URL
                  </label>
                  <div className="relative group">
                    <input
                      type="text"
                      value={coverImageUrl}
                      onChange={(e) => setCoverImageUrl(e.target.value)}
                      placeholder="https://images.unsplash.com/..."
                      className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-sky-500 transition-all pr-14 font-medium text-sky-900"
                    />
                    <div className="absolute right-6 top-1/2 -translate-y-1/2">
                      {coverImageUrl ? (
                        <CheckCircle size={18} className="text-emerald-500 animate-pulse" />
                      ) : (
                        <ImageIcon size={18} className="text-gray-300" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-sky-950 uppercase tracking-widest">Narrative Payload</label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Once upon a time..."
                    required
                    rows="12"
                    className="w-full px-8 py-6 bg-gray-50 border-2 border-transparent rounded-[2rem] focus:ring-4 focus:ring-sky-500/10 focus:border-sky-500 focus:bg-white transition-all resize-none font-serif leading-loose text-lg text-sky-950 shadow-inner"
                  ></textarea>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`
                        flex-grow py-5 rounded-[1.5rem] font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl flex items-center justify-center gap-4 transition-all transform active:scale-95
                        ${isLoading ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-sky-950 text-white hover:bg-sky-800 shadow-sky-950/20'}
                      `}
                  >
                    {isLoading ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <>
                        <Save size={18} />
                        Publish Discovery
                      </>
                    )}
                  </button>
                  <Link
                    to="/view"
                    className="px-10 py-5 bg-white border-2 border-gray-100 text-sky-950 rounded-[1.5rem] font-black text-[10px] uppercase tracking-widest hover:bg-gray-50 transition-all flex items-center justify-center shadow-lg"
                  >
                    View Registry
                  </Link>
                </div>
              </form>
            </div>
          </div>

          {/* Live Preview Column */}
          <div className="lg:col-span-12 xl:col-span-5 space-y-6">
            <div className="sticky top-10">
              <div className="flex items-center justify-between mb-6 px-4">
                <h3 className="text-[10px] font-black text-sky-950 uppercase tracking-[0.2em] flex items-center gap-3">
                  <Eye size={16} className="text-sky-500" /> Elite Reader Preview
                </h3>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest">Real-time Stream</span>
                </div>
              </div>

              <div className="bg-white rounded-[3.5rem] shadow-2xl border border-gray-100 overflow-hidden ring-12 ring-gray-50 transition-all duration-700 hover:ring-sky-50 group/preview">
                {/* Visual Header */}
                <div className="h-64 bg-gray-100 relative overflow-hidden">
                  {coverImageUrl ? (
                    <img
                      src={coverImageUrl}
                      alt="Story Preview"
                      className="w-full h-full object-cover transition duration-[2s] group-hover/preview:scale-110"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1000&auto=format&fit=crop";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-sky-900/10">
                      <ImageIcon size={64} className="mb-2" strokeWidth={1} />
                      <span className="text-[10px] font-black uppercase tracking-[0.3em]">Visual Hub</span>
                    </div>
                  )}
                  <div className="absolute top-8 left-8">
                    <span className="px-4 py-1.5 bg-white/95 backdrop-blur-md text-sky-600 rounded-full text-[9px] font-black uppercase tracking-widest shadow-xl border border-white/50">
                      {category}
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-40"></div>
                </div>

                {/* Reading Area */}
                <div className="p-12 space-y-6">
                  <h2 className={`text-3xl font-black text-sky-950 leading-[1.1] tracking-tight group-hover/preview:text-sky-600 transition-colors ${!title && 'opacity-10'}`}>
                    {title || 'Narrative Title Pending...'}
                  </h2>

                  <div className="flex items-center gap-4 py-5 border-y border-gray-50">
                    <div className="w-10 h-10 rounded-2xl bg-sky-950 flex items-center justify-center text-white text-xs font-black shadow-lg shadow-sky-950/20">
                      {author.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-[11px] font-black text-sky-950 uppercase tracking-tight leading-none mb-1">{author}</p>
                      <p className="text-[10px] text-gray-400 font-bold tracking-tight uppercase">Elite Contributor • Editorial Preview</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <p className={`text-gray-400 font-serif leading-[2] text-lg line-clamp-[8] italic ${!content && 'opacity-10'}`}>
                      "{content || 'Awaiting thematic payload for editorial review. Every word written here will synchronize across the global feed instantly...'}"
                    </p>
                  </div>

                  <div className="pt-8 flex gap-3">
                    <div className="w-12 h-1.5 bg-sky-600 rounded-full transition-all group-hover/preview:w-20"></div>
                    <div className="w-3 h-1.5 bg-gray-100 rounded-full"></div>
                    <div className="w-3 h-1.5 bg-gray-100 rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Security Badge */}
              <div className="mt-8 p-8 bg-gradient-to-br from-sky-950 to-sky-900 rounded-[2.5rem] text-white relative overflow-hidden group/badge shadow-2xl shadow-sky-950/20">
                <div className="relative z-10">
                  <h4 className="font-black text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                    <Sparkles className="text-amber-400" size={16} /> Elite Publication
                  </h4>
                  <p className="text-[10px] text-sky-200/50 mt-3 leading-relaxed font-bold italic uppercase tracking-tighter">
                    Story artifacts are optimized for global delivery and high-definition visual synchronization across all platform endpoints.
                  </p>
                </div>
                <PenTool className="absolute -right-6 -bottom-6 text-white/5 w-32 h-32 group-hover/badge:rotate-12 transition duration-700" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminDashboard>
  );
};

const Plus = ({ size, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

export default AddBlog;
